'use client';

import { Box, Stack, Text } from '@mantine/core';
import { detectEmbeds } from '@/lib/embed-utils';
import { normalizeLineBreaks } from '@/lib/text-utils';

interface BioWithEmbedsProps {
  bio: string;
  maxEmbeds?: number;
  className?: string;
}

interface ContentSegment {
  type: 'text' | 'youtube' | 'spotify';
  content: string;
  embedUrl?: string;
}

/**
 * Component that renders mentor bio with automatic YouTube and Spotify embeds
 */
export function BioWithEmbeds({ bio, maxEmbeds = 2, className }: BioWithEmbedsProps) {
  const embeds = detectEmbeds(bio, maxEmbeds);
  
  // Split bio into segments (text and embeds)
  const segments: ContentSegment[] = [];
  let remainingText = bio;
  
  // Sort embeds by their position in the text
  const embedsWithPosition = embeds.map(embed => ({
    ...embed,
    position: bio.indexOf(embed.originalUrl),
  })).sort((a, b) => a.position - b.position);

  let lastIndex = 0;
  for (const embed of embedsWithPosition) {
    const urlIndex = remainingText.indexOf(embed.originalUrl);
    if (urlIndex === -1) continue;

    // Add text before the URL
    const textBefore = remainingText.substring(0, urlIndex);
    if (textBefore) {
      segments.push({
        type: 'text',
        content: textBefore,
      });
    }

    // Add the embed
    segments.push({
      type: embed.type,
      content: embed.originalUrl,
      embedUrl: embed.embedUrl,
    });

    // Update remaining text
    remainingText = remainingText.substring(urlIndex + embed.originalUrl.length);
  }

  // Add any remaining text
  if (remainingText) {
    segments.push({
      type: 'text',
      content: remainingText,
    });
  }

  // If no embeds found, just show the text
  if (segments.length === 0) {
    segments.push({
      type: 'text',
      content: bio,
    });
  }

  return (
    <Stack gap="md">
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          return (
            <Text key={index} size="md" c="gray.7" className={className} style={{ whiteSpace: 'pre-wrap' }}>
              {normalizeLineBreaks(segment.content)}
            </Text>
          );
        }

        if (segment.type === 'youtube') {
          return (
            <Box key={index} style={{ width: '100%', maxWidth: '100%' }}>
              <Box
                style={{
                  position: 'relative',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: '8px',
                }}
              >
                <iframe
                  src={segment.embedUrl}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`YouTube video ${index + 1}`}
                />
              </Box>
            </Box>
          );
        }

        if (segment.type === 'spotify') {
          return (
            <Box key={index} style={{ width: '100%', maxWidth: '100%' }}>
              <Box
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src={segment.embedUrl}
                  style={{
                    width: '100%',
                    height: '352px',
                    border: 0,
                  }}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title={`Spotify embed ${index + 1}`}
                />
              </Box>
            </Box>
          );
        }

        return null;
      })}
    </Stack>
  );
}
